/**
 * Spec 10.2.1 §2.2–2.6 — ledger workbench data hooks (cockpit, account view,
 * reconciliation, period close, composer). Reads via the composite verbs added
 * for the chrome; the "Request authorisation" actions route through the A2
 * request verbs (never a direct post) and invalidate the approvals inbox so the
 * new pending row appears in Needs You.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

// ---- Shapes (mirror the engine composite reads) --------------------------

export interface LedgerSummary {
  id: string; slug: string; name: string; profile: string; currency: string;
  post_authorisation: string; status: string;
  bank_name: string | null; bank_bsb: string | null; bank_account_no: string | null;
  open_accounts: number; pending_authorisations: number;
}
export interface LedgerAccount {
  id: string; account_no: string; kind: string; title_name: string; reference: string | null;
  responsible_user: string | null; balance_minor: number; available_minor: number;
  uncleared_minor: number; held_minor: number; status: string;
  title_address?: string | null; description?: string | null; other_party?: string | null;
  normal_side: string;
}
export interface LedgerTxn {
  id: string; txn_no: number | null; type: string; status: string; doc_no: string | null;
  effective_date: string; amount_minor: number | null; counterparty_name: string | null;
  reason: string | null; posted_at: string | null;
}
export interface LedgerCockpit {
  ledger: {
    id: string; slug: string; name: string; profile: string; currency: string;
    post_authorisation: string; authorised_signatories: string;
    bank_name: string | null; bank_bsb: string | null; bank_account_no: string | null; status: string;
  };
  status: {
    ledger_id: string; name: string; profile: string;
    open_periods: { id: string; period: string; status: string; due_by: string | null }[];
    draft_transactions: number; unbanked_deposits: number;
    unpresented_instruments: { id: string; kind: string; number: string; amount_minor: number; payee: string | null; issued_at: string }[];
    uncleared_total_minor: number;
  };
  accounts: LedgerAccount[];
  recent_transactions: LedgerTxn[];
  exceptions: { exceptions: Record<string, unknown>[]; count: number };
  pending_authorisations: number;
}
export interface LedgerAccountDetail {
  account: LedgerAccount;
  balance_minor: number; available_minor: number; uncleared_minor: number; held_minor: number;
  entries: Record<string, unknown>[];
}
export interface PeriodClose {
  ledger_id: string; period: string; period_status: string;
  closed_at: string | null; closed_by: string | null; due_by: string | null;
  checklist: {
    trial_balance_nil: boolean; reconciliation_finalised: boolean;
    reconciliation: { id: string; variance_minor: number } | null;
    exceptions_count: number; pack_rendered: boolean;
  };
  artifacts: { id: string; kind: string; file_id: string; sha256: string; page_count: number | null; reviewed_by: string | null }[];
}

async function read<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

export function useLedgers() {
  return useQuery({ queryKey: ["ledger", "list"], staleTime: 15_000, queryFn: () => read<{ ledgers: LedgerSummary[] }>("list_ledgers") });
}
export function useLedgerCockpit(ledgerId: string | undefined) {
  return useQuery({ queryKey: ["ledger", "cockpit", ledgerId], enabled: !!ledgerId, staleTime: 10_000, refetchInterval: 30_000, queryFn: () => read<LedgerCockpit>("get_ledger_cockpit", { ledger_id: ledgerId }) });
}
export function useLedgerAccount(accountId: string | undefined) {
  return useQuery({ queryKey: ["ledger", "account", accountId], enabled: !!accountId, staleTime: 10_000, queryFn: () => read<LedgerAccountDetail>("get_ledger_account", { account_id: accountId }) });
}
export function useLedgerPeriodClose(ledgerId: string | undefined, period: string | undefined) {
  return useQuery({ queryKey: ["ledger", "period-close", ledgerId, period], enabled: !!ledgerId && !!period, staleTime: 10_000, queryFn: () => read<PeriodClose>("get_ledger_period_close", { ledger_id: ledgerId, period }) });
}

export interface PeriodHistoryEntry {
  id: string; period: string; status: string;
  opening_balance_minor: number | null; closing_balance_minor: number | null;
  closed_at: string | null; closed_by: string | null; due_by: string | null;
  artifacts: { id: string; kind: string; file_id: string; sha256: string; page_count: number | null; prepared_at: string; prepared_by: string }[];
}
export interface PeriodHistory { ledger_id: string; name: string; periods: PeriodHistoryEntry[] }
/** §10.4 — every period (open + closed) with its sealed pack manifest. Read-only; reaching a closed period never reopens it. */
export function useLedgerPeriodHistory(ledgerId: string | undefined) {
  return useQuery({ queryKey: ["ledger", "period-history", ledgerId], enabled: !!ledgerId, staleTime: 10_000, queryFn: () => read<PeriodHistory>("list_ledger_periods", { ledger_id: ledgerId }) });
}
export function useLedgerReconciliation(reconId: string | undefined) {
  return useQuery({ queryKey: ["ledger", "reconciliation", reconId], enabled: !!reconId, staleTime: 10_000, queryFn: () => read<{ reconciliation: Record<string, unknown>; items: Record<string, unknown>[] }>("get_ledger_reconciliation", { reconciliation_id: reconId }) });
}

export interface ReconState {
  ledger_id: string;
  reconciliation: { id: string; period_id: string; as_at_date: string; bank_statement_balance_minor: number; cashbook_balance_minor: number; reconciled_balance_minor: number; variance_minor: number; status: string } | null;
  items: { id: string; kind: string; description: string; amount_minor: number }[];
}
export function useLedgerCurrentReconciliation(ledgerId: string | undefined) {
  return useQuery({ queryKey: ["ledger", "reconciliation", "current", ledgerId], enabled: !!ledgerId, staleTime: 10_000, queryFn: () => read<ReconState>("get_ledger_current_reconciliation", { ledger_id: ledgerId }) });
}

// ---- "Request authorisation" actions (A2 request verbs) ------------------

function useRequestMutation<V>(verb: string, argsOf: (v: V) => Record<string, unknown>, invalidate: (qc: ReturnType<typeof useQueryClient>) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => {
      const r = await mcpClient.toolsCall(verb, argsOf(v));
      if (r.isError) throw new Error(r.content?.[0]?.text ?? `${verb} failed`);
      return r.structuredContent;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ledger", "authorisations", "pending"] });
      invalidate(qc);
    },
  });
}

export function useRequestPeriodClose() {
  // Spec 10.3.1 Stage 2 — no artifacts passed: the server renders the statutory
  // pack (Browser Run → R2) and binds it into the authorisation.
  return useRequestMutation<{ ledgerId: string; period: string }>(
    "request_ledger_period_close",
    (v) => ({ ledger_id: v.ledgerId, period: v.period, requested_via: "period_close_workbench" }),
    (qc) => void qc.invalidateQueries({ queryKey: ["ledger", "period-close"] }),
  );
}

export interface PackArtifact { kind: string; file_id: string; sha256: string; page_count: number | null }
/** Spec 10.3.1 Stage 2 — reprint the pack as COPY-marked PDFs (r 38.6). */
export function useReprintPeriodPack() {
  return useMutation({
    mutationFn: async (v: { ledgerId: string; period: string }) => {
      const r = await mcpClient.toolsCall<{ artifacts: PackArtifact[] }>("render_period_pack", { ledger_id: v.ledgerId, period: v.period });
      return (r.structuredContent as { artifacts: PackArtifact[] })?.artifacts ?? [];
    },
  });
}
export function useRequestReconciliationFinalise() {
  return useRequestMutation<{ reconciliationId: string }>(
    "request_ledger_reconciliation_finalise",
    (v) => ({ reconciliation_id: v.reconciliationId, requested_via: "reconciliation_workbench" }),
    (qc) => void qc.invalidateQueries({ queryKey: ["ledger", "reconciliation"] }),
  );
}
export function useRequestAccountClose() {
  return useRequestMutation<{ accountId: string }>(
    "request_ledger_account_close",
    (v) => ({ account_id: v.accountId, requested_via: "account_view" }),
    (qc) => void qc.invalidateQueries({ queryKey: ["ledger", "account"] }),
  );
}

export interface ComposeInput {
  type: "receipt" | "payment" | "journal";
  ledgerId: string;
  effective_date: string;
  reason: string;
  method?: string;
  counterparty_name?: string; // receipt drawer
  payee?: string; // payment payee
  instrument_kind?: string; // payment
  lines?: { account_id: string; amount_minor: number }[]; // receipt/payment
  from_account_id?: string; // journal
  to_account_id?: string; // journal
  amount_minor?: number; // journal
  authorised_by?: string;
}

/** Compose a draft (receipt/payment/journal) then request authorisation to post it. */
export function useComposeAndRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: ComposeInput) => {
      const draftVerb = v.type === "receipt" ? "draft_ledger_receipt" : v.type === "payment" ? "draft_ledger_payment" : "draft_ledger_journal";
      const draftArgs: Record<string, unknown> = { ledger_id: v.ledgerId, effective_date: v.effective_date, reason: v.reason };
      if (v.type === "receipt") { draftArgs.method = v.method; draftArgs.counterparty_name = v.counterparty_name; draftArgs.lines = v.lines; }
      else if (v.type === "payment") { draftArgs.payee = v.payee; if (v.instrument_kind) draftArgs.instrument = { kind: v.instrument_kind }; draftArgs.lines = v.lines; draftArgs.authorised_by = v.authorised_by; }
      else { draftArgs.from_account_id = v.from_account_id; draftArgs.to_account_id = v.to_account_id; draftArgs.amount_minor = v.amount_minor; draftArgs.authorised_by = v.authorised_by; }

      const draft = await mcpClient.toolsCall<{ txn: { id: string } }>(draftVerb, draftArgs);
      if (draft.isError) throw new Error(draft.content?.[0]?.text ?? `${draftVerb} failed`);
      const txnId = (draft.structuredContent as { txn: { id: string } }).txn.id;

      const req = await mcpClient.toolsCall("request_ledger_post", { txn_id: txnId, requested_via: "composer" });
      if (req.isError) throw new Error(req.content?.[0]?.text ?? "request_ledger_post failed");
      return req.structuredContent;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ledger", "authorisations", "pending"] });
      void qc.invalidateQueries({ queryKey: ["ledger", "cockpit"] });
    },
  });
}
